/**
 * <Drawer> — canonical side-panel.
 *
 * Built on @radix-ui/react-dialog (same primitive as <Modal>) so we get
 * the same focus trap, focus restore, ESC handling, and scroll lock.
 * Resolves Phase-1 findings:
 *   C4 — 99 rogue `fixed inset-0` modal/drawer patterns
 *   C7 — focus trap missing in 80% of drawers
 *
 * Composition (compound API):
 *
 *   <Drawer open={isOpen} onOpenChange={setOpen} side='right' size='md'>
 *     <Drawer.Header
 *       title='Work order WO-1042'
 *       description='Routine maintenance — Pump 7'
 *     />
 *     <Drawer.Body>
 *       <WorkOrderDetail …/>
 *     </Drawer.Body>
 *     <Drawer.Footer>
 *       <Button variant='secondary'>Close</Button>
 *       <Button variant='primary'>Save</Button>
 *     </Drawer.Footer>
 *   </Drawer>
 *
 * Sides: right (default — most common in SMMS) or left.
 * Sizes: sm (360px) / md (480px / default) / lg (640px) / detail (800px).
 *
 * Run 2 (mobile-remediation) additions — resolves audit findings F-02/F-08
 * (same class of bug as `<Modal>`, see its doc comment):
 *   - `h-[100dvh]` instead of `h-full` (was resolving against the LAYOUT
 *     viewport via `top-0 bottom-0`, which iOS Safari does not shrink for
 *     browser chrome).
 *   - Height also tracks `window.visualViewport` live so the drawer shrinks
 *     to the visible area when the on-screen keyboard opens (an
 *     over-constrained `top/bottom/height` box resolves in the inline
 *     `height`'s favor per the CSS spec, so this safely wins over the
 *     `bottom-0` side class without removing it).
 *   - `Drawer.Header`/`Drawer.Footer` get additive `env(safe-area-inset-*)`
 *     padding — a no-op on non-notched devices.
 *   - `Drawer.Footer` now renders via the shared `<ActionFooter>` shell.
 *
 * Spec: docs/design-system/08-modals-drawers-feedback.md.
 */

import * as RadixDialog from '@radix-ui/react-dialog';
import { type ReactNode, type HTMLAttributes, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Heading } from '../../primitives/Heading';
import { Text } from '../../primitives/Text';
import { Icon } from '../../primitives/Icon';
import { ActionFooter } from '../ActionFooter';
import { useVisualViewport } from '../../hooks/useVisualViewport';

export type DrawerSide = 'right' | 'left';
export type DrawerSize = 'sm' | 'md' | 'lg' | 'detail';

export interface DrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: DrawerSide;
  size?: DrawerSize;
  disableBackdropClose?: boolean;
  disableEscClose?: boolean;
  className?: string;
  children: ReactNode;
}

const SIZE_CLASSES: Record<DrawerSize, string> = {
  sm: 'w-[min(92vw,360px)]',
  md: 'w-[min(92vw,480px)]',
  lg: 'w-[min(92vw,640px)]',
  detail: 'w-[min(96vw,800px)]',
};

const SIDE_CLASSES: Record<DrawerSide, string> = {
  right: 'right-0 top-0 bottom-0 data-[state=open]:animate-slide-left',
  left: 'left-0 top-0 bottom-0 data-[state=open]:animate-slide-right',
};

function DrawerRoot({
  open,
  onOpenChange,
  side = 'right',
  size = 'md',
  disableBackdropClose,
  disableEscClose,
  className,
  children,
}: DrawerProps) {
  // Keyboard-aware sizing (F-02/F-08) — see the module doc comment. Skips
  // subscribing while closed (`enabled: open`) so the many Drawer instances
  // that stay mounted-but-closed across the app don't each hold a live
  // `visualViewport` listener.
  const vv = useVisualViewport(open);
  const dynamicHeight = vv.height != null ? `${vv.height}px` : undefined;

  // A11Y-KB-02 fix (2026-08-08, P2 keyboard/SR tail) — ported from Modal.tsx
  // (P1 overlay migration, 2026-08-14): Radix's OWN default close-focus
  // behavior (`@radix-ui/react-dialog`'s `DialogContentModal`) does
  // `event.preventDefault(); context.triggerRef.current?.focus();` — which
  // (a) blocks `FocusScope`'s generic "restore focus to whatever was
  // focused before the scope activated" fallback, and (b) only actually
  // restores anything if the opener was a `<RadixDialog.Trigger>`. This
  // component never renders one (every call site opens the drawer from its
  // OWN plain `<Button onClick={...}>`, per this file's own doc comment's
  // canonical example) — so `context.triggerRef` is always empty and the
  // net effect, for every consumer, is that closing a Drawer silently drops
  // focus to `<body>` instead of "returns to the trigger element on close"
  // as documented above. Capture whatever had focus right before the drawer
  // opened and restore it ourselves; passing `onCloseAutoFocus` here runs
  // BEFORE Radix's own default (see `composeEventHandlers` — the caller-
  // supplied handler runs first, and calling `preventDefault()` there skips
  // Radix's default entirely), so this replaces rather than races with it.
  const openerElementRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) {
      openerElementRef.current = document.activeElement as HTMLElement | null;
    }
  }, [open]);

  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay
          className="fixed inset-0 z-[9000] bg-overlay backdrop-blur-sm data-[state=open]:animate-fade-in"
        />
        <RadixDialog.Content
          // See the identical comment in Modal.tsx — this @radix-ui/react-dialog
          // version never sets `aria-modal` itself; every Drawer usage here is
          // modal, so this is always correct to set statically.
          aria-modal="true"
          onCloseAutoFocus={(e) => {
            const opener = openerElementRef.current;
            if (opener && document.contains(opener) && typeof opener.focus === 'function') {
              e.preventDefault();
              opener.focus();
            }
          }}
          onPointerDownOutside={(e) => {
            if (disableBackdropClose) e.preventDefault();
          }}
          onEscapeKeyDown={(e) => {
            if (disableEscClose) e.preventDefault();
          }}
          style={dynamicHeight != null ? { height: dynamicHeight } : undefined}
          className={cn(
            'fixed z-[9001] flex flex-col bg-surface text-text shadow-2xl border-l border-border-subtle',
            'h-[100dvh]',
            SIDE_CLASSES[side],
            SIZE_CLASSES[size],
            side === 'left' && 'border-l-0 border-r border-border-subtle',
            className,
          )}
        >
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
DrawerRoot.displayName = 'Drawer';

interface DrawerHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  showClose?: boolean;
  closeLabel?: string;
  /** Right-aligned actions in the header (e.g. status pill, edit button). */
  actions?: ReactNode;
}

function DrawerHeader({
  title,
  description,
  eyebrow,
  showClose = true,
  closeLabel = 'Close drawer',
  actions,
  className,
  ...rest
}: DrawerHeaderProps) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 px-5 pb-3 border-b border-border-subtle',
        // Additive safe-area top padding (F-02) — the drawer sits flush
        // against the true top edge of the screen (`top-0`), which can be
        // behind a notch/Dynamic Island in landscape. Resolves to the
        // normal `pt-5` (1.25rem) on non-notched devices.
        'pt-[calc(1.25rem+env(safe-area-inset-top,0px))]',
        className,
      )}
      {...rest}
    >
      <div className="flex flex-col gap-1 min-w-0 flex-1">
        {eyebrow && (
          <Text variant="eyebrow" tone="muted">
            {eyebrow}
          </Text>
        )}
        <RadixDialog.Title asChild>
          <Heading level={3}>{title}</Heading>
        </RadixDialog.Title>
        {description && (
          <RadixDialog.Description asChild>
            <Text variant="body-sm" tone="muted">
              {description}
            </Text>
          </RadixDialog.Description>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {actions}
        {showClose && (
          <RadixDialog.Close
            aria-label={closeLabel}
            className={cn(
              'rounded-md p-1 text-muted hover:bg-elevated hover:text-text',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
            )}
          >
            <Icon as={X} size="sm" label={closeLabel} />
          </RadixDialog.Close>
        )}
      </div>
    </div>
  );
}
DrawerHeader.displayName = 'Drawer.Header';

function DrawerBody({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  // tabIndex={0} satisfies WCAG 2.1.1 (keyboard) for scrollable regions —
  // without it, keyboard-only users can't arrow-scroll the body when its
  // children aren't focusable (axe rule: scrollable-region-focusable).
  // role="group" + aria-label keeps screen readers from announcing the
  // bare div as an unnamed landmark.
  return (
    <div
      role="group"
      aria-label="Drawer content"
      // intentional keyboard-scrollable region (axe scrollable-region-focusable); documented false positive in eslint.config.js
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      className={cn('flex-1 overflow-y-auto px-5 py-4 focus:outline-none', className)}
      {...rest}
    >
      {children}
    </div>
  );
}
DrawerBody.displayName = 'Drawer.Body';

function DrawerFooter({ className, children, ...rest }: HTMLAttributes<HTMLDivElement>) {
  // Delegates to the shared <ActionFooter> shell (F-02) — same treatment as
  // Modal.Footer.
  return (
    <ActionFooter variant="panel" className={className} {...rest}>
      {children}
    </ActionFooter>
  );
}
DrawerFooter.displayName = 'Drawer.Footer';

type DrawerComponent = typeof DrawerRoot & {
  Header: typeof DrawerHeader;
  Body: typeof DrawerBody;
  Footer: typeof DrawerFooter;
};

export const Drawer = DrawerRoot as DrawerComponent;
Drawer.Header = DrawerHeader;
Drawer.Body = DrawerBody;
Drawer.Footer = DrawerFooter;

export default Drawer;
